import { graphql } from '@/generated/graphql';

export const GET_ORDER_COMMENTS = graphql(`
  query GetOrderComments($orderId: Int!) {
    order_comments(where: { orderId: { _eq: $orderId } }, order_by: { createdAt: desc }) {
      id
      authorId
      externalAuthor
      comment
      createdAt
      source
      user {
        name
      }
    }
  }
`);

export const GET_ORDER_HISTORY = graphql(`
  query GetOrderHistory($offset: Int = 0, $orderId: Int!) {
    order_change(
      limit: 20
      offset: $offset
      where: { orderId: { _eq: $orderId } }
      order_by: { createdAt: desc }
    ) {
      id
      editor {
        id
        name
      }
      new
      old
      createdAt
    }
    order_change_aggregate(where: { orderId: { _eq: $orderId } }) {
      aggregate {
        count
      }
    }
    driver_order_interaction(where: { order_id: { _eq: $orderId } }) {
      id
      contact_type
      createdAt: created_at
      user {
        name
      }
    }
    user_views_log(where: { orderId: { _eq: $orderId } }) {
      id
      orderId
      userId
      createdAt
      user {
        name
      }
    }
  }
`);

export const GET_ORDERS = graphql(`
  query GetOrders(
    $limit: Int = 10
    $offset: Int = 0
    $where: order_bool_exp = {}
    $sorters: [order_order_by!]!
  ) {
    order(order_by: $sorters, limit: $limit, offset: $offset, where: $where) {
      totalCents
      comment
      id
      createdAt
      updatedAt
      shortCode
      isSaved
      resale
      rescheduleCount
      paymentType
      status {
        name
        status_transitions {
          next
        }
      }
      createdAt
      plannedDate
      plannedDateDuration
      deliveredDate
      driver {
        name
        id
        blocked
      }
      positions {
        product {
          id
          name
        }
      }
      client {
        id
        phone
        fullname
        districtId
        address {
          city
          comments
          country
          district
          id
          lat
          lng
          street
          state
        }
      }
      delivery_provider {
        short_name
        display_name
      }
    }
    order_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_META = graphql(`
  query GetMeta($departmentId: Int!) {
    user(
      where: {
        roles: { _contains: "driver" }
        blocked: { _eq: false }
        departments_users: { departmentId: { _eq: $departmentId } }
      }
      order_by: { blocked: asc, name: asc }
    ) {
      id
      name
      blocked
    }
    product(where: { country: { departments: { id: { _eq: $departmentId } } } }) {
      id
      name
    }
  }
`);

export const GET_BLOCKED_DRIVERS = graphql(`
  query GetBlockedDrivers($departmentId: Int!) {
    user(
      where: {
        roles: { _contains: "driver" }
        blocked: { _eq: true }
        departments_users: { departmentId: { _eq: $departmentId } }
      }
      order_by: { name: asc }
    ) {
      id
      name
      blocked
    }
  }
`);

export const GET_STATUSES = graphql(`
  query GetStatuses($ordersWhere: order_bool_exp!) {
    status {
      name
      orders_aggregate(where: $ordersWhere) {
        aggregate {
          count
        }
      }
    }
    DriverAssignedExpress: order_aggregate(
      where: {
        _and: [
          $ordersWhere,
          { statusName: { _eq: "DriverAssigned" } },
          { delivery_provider: { short_name: { _eq: "DTK_EXPRESS" } } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    DriverConfirmedExpress: order_aggregate(
      where: {
        _and: [
          $ordersWhere,
          { statusName: { _eq: "DriverConfirmed" } },
          { delivery_provider: { short_name: { _eq: "DTK_EXPRESS" } } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    DriverOnTheWayExpress: order_aggregate(
      where: {
        _and: [
          $ordersWhere,
          { statusName: { _eq: "DriverOnTheWay" } },
          { delivery_provider: { short_name: { _eq: "DTK_EXPRESS" } } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_ORDER_BY_ID_WITH_DRIVERS_WAREHOUSES = graphql(`
  query GetOrderByIdWithDriversWarehouses($id: Int!, $departmentId: Int!) {
    order_by_pk(id: $id) {
      id
      comment
      paymentType
      isSaved
      resale
      departmentId
      department {
        country {
          id
        }
      }
      umbrellaID
      vendorDatalinkId
      totalCents
      reasonRefusal
      shortCode
      createdAt
      order_photos {
        downloadURL
        id
      }
      status {
        name
        status_transitions {
          next
        }
      }
      plannedDate
      plannedDateDuration
      positions {
        id
        product {
          id
          name
        }
        quantity
        productID
        totalPrice
      }
      order_changes {
        id
      }
      client {
        id
        phone
        fullname
        districtId
        address {
          id
          city
          comments
          country
          district
          state
          street
          lat
          lng
          client {
            district {
              name
            }
          }
        }
      }
      driver {
        id
        name
        email
        blocked
      }
      warehouse {
        name
        id
      }
      delivery_provider {
        short_name
        display_name
      }
    }
    user(
      where: {
        roles: { _contains: "driver" }
        blocked: { _eq: false }
        departments_users: { departmentId: { _eq: $departmentId } }
      }
      order_by: { blocked: asc, name: asc }
    ) {
      id
      name
      blocked
    }
    product(where: { country: { departments: { id: { _eq: $departmentId } } } }) {
      id
      name
    }
  }
`);

export const GET_ORDER_LIST_BY_ID_WITH_DRIVERS_WAREHOUSES = graphql(`
  query GetOrderListByIdWithDriversWarehouses($ids: [Int!], $departmentId: Int!) {
    order(where: { id: { _in: $ids } }) {
      id
      comment
      createdAt
      totalCents
      shortCode
      status {
        name
        status_transitions {
          next
        }
      }
      plannedDate
      plannedDateDuration
      client {
        id
        phone
        fullname
        address {
          id
          city
          comments
          country
          district
          state
          street
          lat
          lng
        }
      }
      driver {
        id
        name
        email
        blocked
      }
      positions {
        product {
          name
          id
        }
        quantity
      }
    }
    user(
      where: {
        roles: { _contains: "driver" }
        blocked: { _eq: false }
        departments_users: { departmentId: { _eq: $departmentId } }
      }
      order_by: { blocked: asc, name: asc }
    ) {
      id
      name
      blocked
    }
  }
`);

export const GET_DISTRICT_SCHEDULE_AND_WORKLOAD = graphql(`
  query GetDistrictScheduleAndWorkload(
    $districtId: String!
    $plannedDate: timestamptz!
    $nextDay: timestamptz!
  ) {
    district_by_pk(id: $districtId) {
      id
      loadFactor
      weekday_window_districts {
        weekday_window {
          weekday
          driver_window {
            timeDuration
          }
        }
      }
    }
    district_workload(
      where: { timeperiod: { _gte: $plannedDate, _lt: $nextDay }, districtId: { _eq: $districtId } }
      order_by: { timeperiod: asc }
    ) {
      districtId
      loadFactor
      timeperiod
      workload
    }
  }
`);

export const GET_DRIVERS_WORKLOAD = graphql(`
  query GetDriversWorkload(
    $districtId: String!
    $driverId: String!
    $startOfWeek: timestamptz!
    $endOfWeek: timestamptz!
  ) {
    driver_workload_for(
      args: { district_id: $districtId }
      where: { driverId: { _eq: $driverId }, timeperiod: { _gte: $startOfWeek, _lte: $endOfWeek } }
      order_by: { timeperiod: asc }
    ) {
      workload
      timeperiod
      timeZone
      driverId
    }
  }
`);

export const GET_PAYMENT_TYPES = graphql(`
  query GetPaymentTypes {
    payment_type {
      value
    }
  }
`);

export const EXPRESS_NEW_ORDERS_MULTI_QUERY = graphql(`
  query ExpressNewOrdersMultiQuery($departmentIds: [Int!]!) {
    order(
      where: {
        statusName: { _in: ["New", "DriverAssigned"] }
        departmentId: { _in: $departmentIds }
        delivery_provider: { short_name: { _eq: "DTK_EXPRESS" } }
      }
      order_by: { createdAt: desc }
    ) {
      id
      statusName
      createdAt
      shortCode
      departmentId
      department {
        id
        name
      }
      delivery_provider {
        short_name
        display_name
      }
    }
  }
`);

export const NEW_READY_AND_PICKED_ORDERS_MULTI_QUERY = graphql(`
  query NewReadyAndPickedOrdersMultiQuery($departmentIds: [Int!]!) {
    order(
      where: { statusName: { _eq: "ReadyPicking" }, departmentId: { _in: $departmentIds } }
      order_by: { createdAt: desc }
    ) {
      id
      statusName
      createdAt
      shortCode
      departmentId
      department {
        id
        name
      }
    }
  }
`);
